import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AuthService,
    EmergencyDetailsDto,
    EmployeesClient, PermissionType,
    UpdateEmergencyDetailsCommand
} from '@services';
import notify from 'devextreme/ui/notify';
import {
    DxTextBoxModule,
    DxValidationGroupModule,
    DxValidationGroupComponent
} from 'devextreme-angular';
import { Observable, throwError, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
    selector: 'app-emergency-details',
    templateUrl: './emergency-details.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DxTextBoxModule,
        DxValidationGroupModule
    ]
})
export class EmergencyDetailsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();

    @ViewChild('validationGroup', { static: false }) validationGroup: DxValidationGroupComponent;

    details: EmergencyDetailsDto;
    isLoading = false;

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getEmergencyDetails(this.userId).subscribe({
            next: (data) => {
                this.details = data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                notify('Failed to load emergency details', 'error', 3000);
            }
        });
    }

    public save(): Observable<void> {
        if (this.isReadOnly) return of(void 0);
        if (!this.validationGroup) return of();

        const result = this.validationGroup.instance.validate();
        if (!result.isValid) {
            notify({ message: 'Please fix emergency details errors', position: 'bottom center' }, 'error', 3000);
            return throwError(() => new Error('Validation failed'));
        }

        const command = new UpdateEmergencyDetailsCommand({
            userId: this.userId,
            emergencyContactName: this.details.emergencyContactName,
            emergencyContactNumber: this.details.emergencyContactNumber,
            medicalConditions: this.details.medicalConditions
        });

        return this.employeesClient.updateEmergencyDetails(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{ message: 'Failed to update emergency details' }];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange() {
        this.isDataChanged.emit(true);
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.UserEmergencyDetailsOwn, PermissionType.UserEmergencyDetailsOthers);
    }
}

